import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "../components/ui/calendar";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Mail, 
  User, 
  UserCheck, 
  Phone, 
  Send,
  Minus,
  Plus,
  Gift,
  Puzzle,
  ShoppingBasket,
  CalendarDays,
  Clock
} from "lucide-react";
import { format, getDay, isWeekend } from "date-fns";
import { es } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MATERIALS = [
  { id: "bolos", label: "Bolos", icon: Gift, max: 999 },
  { id: "juguetes", label: "Juguetes", icon: Puzzle, max: 999 },
  { id: "despensas", label: "Material", icon: ShoppingBasket, max: 999 },
];

// Horarios de entrega: 14:00 a 17:30, cada 30 minutos
const PICKUP_TIMES = [
  "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30"
];

// Días festivos obligatorios México 2026
const MEXICAN_HOLIDAYS = [
  "2026-01-01", "2026-02-02", "2026-03-16", "2026-05-01",
  "2026-09-16", "2026-11-17", "2026-12-25"
];

const MaterialCounter = ({ value, onChange, max, label, icon: Icon }) => (
  <div className="border border-[#E5E5E5] rounded-lg p-4 flex flex-col items-center gap-2 hover:border-[#EA580C] transition-colors bg-white" data-testid={`material-${label.toLowerCase()}`}>
    <div className="flex items-center gap-2 w-full">
      <Icon className="w-14 h-14 text-[#0A0A0A] flex-shrink-0" strokeWidth={1.2} />
      <span className="text-sm font-bold text-[#0A0A0A]">{label}</span>
    </div>
    <div className="flex items-center gap-1 mt-1">
      <button
        type="button"
        onClick={() => onChange(Math.max(0, value - 1))}
        className="w-9 h-9 flex items-center justify-center rounded-md bg-[#F5F5F5] hover:bg-[#E5E5E5] transition-colors text-[#EA580C]"
        data-testid={`minus-${label.toLowerCase()}`}
      >
        <Minus className="w-4 h-4" />
      </button>
      <input
        type="number"
        min="0"
        max={max}
        value={value}
        onChange={(e) => {
          const v = parseInt(e.target.value) || 0;
          onChange(Math.max(0, Math.min(max, v)));
        }}
        className="w-12 h-9 text-center text-lg font-bold text-[#0A0A0A] border border-[#E5E5E5] rounded-md focus:outline-none focus:border-[#EA580C] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        data-testid={`qty-${label.toLowerCase()}`}
      />
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className="w-9 h-9 flex items-center justify-center rounded-md bg-[#F5F5F5] hover:bg-[#E5E5E5] transition-colors text-[#EA580C] disabled:opacity-30"
        data-testid={`plus-${label.toLowerCase()}`}
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  </div>
);

const ADMIN_WHATSAPP = "523319665178";

const SuppliesPage = () => {
  const [email, setEmail] = useState("");
  const [referentName, setReferentName] = useState("");
  const [collectorName, setCollectorName] = useState("");
  const [collectorPhone, setCollectorPhone] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [materials, setMaterials] = useState({ bolos: 0, juguetes: 0, despensas: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookedTimes, setBookedTimes] = useState([]);

  const fetchAvailability = useCallback(async (date) => {
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const res = await axios.get(`${API}/supply-requests/availability/${dateStr}`);
      setBookedTimes(res.data.booked_times || []);
    } catch {
      setBookedTimes([]);
    }
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailability(selectedDate);
      setSelectedTime("");
    } else {
      setBookedTimes([]);
    }
  }, [selectedDate, fetchAvailability]);

  const isDateDisabled = (date) => {
    // Past dates
    if (date < new Date()) return true;
    // Weekends (Saturday = 6, Sunday = 0)
    const day = getDay(date);
    if (day === 0 || day === 6) return true;
    // Mexican holidays
    const dateStr = format(date, "yyyy-MM-dd");
    if (MEXICAN_HOLIDAYS.includes(dateStr)) return true;
    return false;
  };

  const updateMaterial = (id, value) => {
    setMaterials(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !referentName || !collectorName || !collectorPhone || !selectedDate || !selectedTime) {
      toast.error("Por favor completa todos los campos requeridos");
      return;
    }

    const totalMaterials = materials.bolos + materials.juguetes + materials.despensas;
    if (totalMaterials === 0) {
      toast.error("Selecciona al menos un material");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/supply-requests`, {
        email,
        referent_name: referentName,
        collector_name: collectorName,
        collector_phone: collectorPhone,
        pickup_date: format(selectedDate, "yyyy-MM-dd"),
        pickup_time: selectedTime,
        materials
      });

      toast.success("Solicitud de insumos enviada exitosamente", {
        description: `Recolección: ${format(selectedDate, "d 'de' MMMM, yyyy", { locale: es })} a las ${selectedTime} hrs`
      });

      // Send WhatsApp notification to admin
      const matParts = [];
      if (materials.bolos > 0) matParts.push(`Bolos: ${materials.bolos}`);
      if (materials.juguetes > 0) matParts.push(`Juguetes: ${materials.juguetes}`);
      if (materials.despensas > 0) matParts.push(`Despensas: ${materials.despensas}`);
      
      const waMsg = encodeURIComponent(
        `*Nueva Solicitud de Insumos*\n` +
        `Casa Ciudadana Zapopan\n\n` +
        `*Referente:* ${referentName}\n` +
        `*Correo:* ${email}\n` +
        `*Persona que recoge:* ${collectorName}\n` +
        `*Teléfono:* ${collectorPhone}\n` +
        `*Fecha:* ${format(selectedDate, "d 'de' MMMM, yyyy", { locale: es })}\n` +
        `*Hora:* ${selectedTime} hrs\n` +
        `*Material:* ${matParts.join(', ')}`
      );
      window.open(`https://wa.me/${ADMIN_WHATSAPP}?text=${waMsg}`, '_blank');

      setEmail("");
      setReferentName("");
      setCollectorName("");
      setCollectorPhone("");
      setSelectedDate(null);
      setSelectedTime("");
      setMaterials({ bolos: 0, juguetes: 0, despensas: 0 });
    } catch (error) {
      console.error("Error:", error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409) {
        toast.error("Horario no disponible", { description: detail });
        fetchAvailability(selectedDate);
      } else {
        toast.error("Error al enviar la solicitud", {
          description: "Por favor intenta de nuevo"
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Mobile banner with image */}
      <div className="lg:hidden">
        <img 
          src="https://customer-assets.emergentagent.com/job_politica-reservas/artifacts/h5jlkfxk_IMG_2165.jpeg"
          alt="Casa Ciudadana Zapopan - Solicitud de Insumos"
          className="w-full h-auto"
        />
      </div>

      {/* Desktop layout with sidebar image */}
      <div className="flex flex-row">
        <div className="hidden lg:flex lg:w-[45%] xl:w-1/2 sticky top-14 overflow-hidden flex-col bg-[#e8c9a8]" style={{ height: 'calc(100vh - 56px)' }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_politica-reservas/artifacts/5385qzrk_IMG_2166.jpeg"
            alt="Casa Ciudadana Zapopan - Solicitud de Insumos"
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="lg:w-[55%] xl:w-1/2 w-full">
          <div className="p-5 md:p-8 lg:p-12">
        <form onSubmit={handleSubmit} className="max-w-xl mx-auto space-y-8 bg-white border border-[#E5E5E5] p-6 md:p-8" data-testid="supplies-form">
          
          {/* Section 1: Contact Info */}
          <div className="space-y-4">
            <h2 className="heading-h3 flex items-center gap-2 text-[#EA580C]">
              <span className="w-7 h-7 bg-[#EA580C] text-white text-sm font-bold flex items-center justify-center flex-shrink-0">1</span>
              Datos de Contacto
            </h2>

            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="supply-email" className="label-text flex items-center gap-1">
                  <Mail className="w-3 h-3" /> Correo Electrónico *
                </Label>
                <Input
                  id="supply-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ejemplo@correo.com"
                  className="input-swiss rounded-none text-sm"
                  data-testid="supply-email"
                  required
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="referent-name" className="label-text flex items-center gap-1">
                  <User className="w-3 h-3" /> Nombre del Referente *
                </Label>
                <Input
                  id="referent-name"
                  type="text"
                  value={referentName}
                  onChange={(e) => setReferentName(e.target.value)}
                  placeholder="Nombre completo del referente"
                  className="input-swiss rounded-none text-sm"
                  data-testid="supply-referent"
                  required
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="collector-name" className="label-text flex items-center gap-1">
                  <UserCheck className="w-3 h-3" /> Persona designada para recolección *
                </Label>
                <Input
                  id="collector-name"
                  type="text"
                  value={collectorName}
                  onChange={(e) => setCollectorName(e.target.value)}
                  placeholder="Nombre de quien recogerá el material"
                  className="input-swiss rounded-none text-sm"
                  data-testid="supply-collector"
                  required
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="collector-phone" className="label-text flex items-center gap-1">
                  <Phone className="w-3 h-3" /> Teléfono de quien recogerá *
                </Label>
                <Input
                  id="collector-phone"
                  type="tel"
                  value={collectorPhone}
                  onChange={(e) => setCollectorPhone(e.target.value)}
                  placeholder="Ej. 33 1234 5678"
                  className="input-swiss rounded-none text-sm"
                  data-testid="supply-phone"
                  required
                />
              </div>
            </div>
          </div>

          {/* Section 2: Date & Time */}
          <div className="space-y-4">
            <h2 className="heading-h3 flex items-center gap-2 text-[#EA580C]">
              <span className="w-7 h-7 bg-[#EA580C] text-white text-sm font-bold flex items-center justify-center flex-shrink-0">2</span>
              Fecha y Hora de Recolección
            </h2>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-shrink-0">
                <div className="border border-[#E5E5E5] inline-block" data-testid="supply-calendar">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    locale={es}
                    disabled={isDateDisabled}
                    className="rounded-none"
                  />
                </div>
              </div>

              <div className="flex-1 space-y-3">
                <div className="space-y-1">
                  <Label className="label-text flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Hora de entrega *
                  </Label>
                  <Select value={selectedTime} onValueChange={setSelectedTime} disabled={!selectedDate}>
                    <SelectTrigger className="input-swiss rounded-none text-sm" data-testid="supply-time-trigger">
                      <SelectValue placeholder="Selecciona hora" />
                    </SelectTrigger>
                    <SelectContent>
                      {PICKUP_TIMES.map((time) => {
                        const isBooked = bookedTimes.includes(time);
                        return (
                          <SelectItem 
                            key={time} 
                            value={time} 
                            disabled={isBooked}
                            data-testid={`supply-time-${time}`}
                            className={isBooked ? "text-[#999] line-through" : ""}
                          >
                            {time} hrs {isBooked ? " — Ocupado" : ""}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {selectedDate && (
                  <p className="text-xs text-[#666]">
                    Fecha: <span className="font-semibold text-[#0A0A0A]">
                      {format(selectedDate, "EEEE d 'de' MMMM, yyyy", { locale: es })}
                    </span>
                  </p>
                )}

                {bookedTimes.length > 0 && (
                  <div className="bg-[#FEF3C7] border border-yellow-300 p-2">
                    <p className="text-xs font-semibold text-[#92400E]">Horarios ocupados este día:</p>
                    {bookedTimes.map((t, i) => (
                      <p key={i} className="text-xs text-[#92400E]">{t} hrs</p>
                    ))}
                  </div>
                )}

                <div className="bg-[#F5F5F5] p-3 text-xs text-[#666] space-y-1">
                  <p className="font-semibold text-[#0A0A0A]">Horario de entrega:</p>
                  <p>Lunes a Viernes, 14:00 - 17:30 hrs (cada 30 min)</p>
                  <p>Sábados, domingos y días festivos no disponibles</p>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Materials */}
          <div className="space-y-4">
            <h2 className="heading-h3 flex items-center gap-2 text-[#EA580C]">
              <span className="w-7 h-7 bg-[#EA580C] text-white text-sm font-bold flex items-center justify-center flex-shrink-0">3</span>
              Selección de Material
            </h2>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {MATERIALS.map((material) => (
                <MaterialCounter
                  key={material.id}
                  value={materials[material.id]}
                  onChange={(val) => updateMaterial(material.id, val)}
                  max={material.max}
                  label={material.label}
                  icon={material.icon}
                />
              ))}
            </div>
          </div>

          {/* Submit */}
          <div className="pb-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#EA580C] text-white py-4 font-bold text-lg uppercase tracking-wide hover:bg-orange-700 transition-colors disabled:bg-[#E5E5E5] disabled:text-[#666] flex items-center justify-center gap-2"
              data-testid="supply-submit"
            >
              <Send className="w-5 h-5" />
              {isSubmitting ? "Enviando..." : "Enviar Solicitud"}
            </button>
          </div>
        </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuppliesPage;
